import React, { useEffect, useState } from "react";
import { adminApi } from "../../../repositories/admin";
import Button from "../../../components/ui/Button";

/**
 * The AD-305 rollback switch. On, every playlist view reads stored data; off, they call
 * SoundCharts live as they did before. Applies from the next page load, and the sync jobs
 * keep writing either way, so switching back loses nothing.
 */
export const PlaylistReadPathCard: React.FC = () => {
  const [readFromDb, setReadFromDb] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi
      .getPlaylistSettings()
      .then((res) => setReadFromDb(Boolean(res?.data?.data?.readFromDb)))
      .catch(() => setError("Could not load the playlist data setting."));
  }, []);

  const toggle = async () => {
    if (readFromDb === null) return;
    const next = !readFromDb;
    const question = next
      ? "Serve every playlist view from stored data? Artists and records that have not synced yet will show no playlists until they do."
      : "Serve every playlist view from live SoundCharts calls again? Each page load will spend quota.";
    if (!window.confirm(question)) return;

    setSaving(true);
    setError(null);
    try {
      const res = await adminApi.updatePlaylistSettings({ readFromDb: next });
      setReadFromDb(Boolean(res?.data?.data?.readFromDb));
    } catch {
      setError("The change was not saved. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 mb-6 bg-white border rounded-lg border-neutral-200">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs text-neutral-500 sm:text-sm">Playlist data source</p>
          <p className="mt-1 text-lg font-bold text-black">
            {readFromDb === null ? "…" : readFromDb ? "Stored data" : "Live SoundCharts calls"}
          </p>
        </div>
        <Button
          variant="secondary"
          loading={saving}
          disabled={readFromDb === null || saving}
          onClick={toggle}
        >
          {readFromDb ? "Switch to live calls" : "Switch to stored data"}
        </Button>
      </div>
      <p className="mt-2 text-xs text-neutral-500">
        Applies to every playlist view from the next page load. Playlist syncs keep collecting
        data either way.
      </p>
      {error && <p className="mt-2 text-sm font-medium text-red-600">{error}</p>}
    </div>
  );
};

export default PlaylistReadPathCard;
